import { faBed, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Axios, { CancelTokenSource } from "axios";
import { Link } from "gatsby";
import filter from "lodash/filter";
import moment from "moment-mini";
import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { useAppSelector } from "../../../hooks";
import { useHotels } from "../../../hooks/useHotels";
import fallbackImage from "../../../images/hotel_fallback.jpg";
import { fetchReservationDetails } from "../../../services/crs";
import {
  resDisplayDateFormat,
  resParseDateFormat,
} from "../../../services/dates";
import { priceFormatter } from "../../../utils/helpers";
import EditReservationButton from "../EditReservationButton/EditReservationButton";
import {
  Container,
  FreeStay,
  HotelName,
  RequestFolioButton,
} from "./ReservationCardHelpers";
import { IReservationCardProps } from "./ReservationCardProps";
import LazyLoadImage from "../LazyLoadImage/LazyLoadImage";
import RequestFolioModal from "../RequestFolioModal/RequestFolioModal";

const ReservationCard = (props: IReservationCardProps) => {
  const {
    reservation,
    type,
    onCancel,
    onHotelDisplay,
    onReservationDetailLoad,
  } = props;
  const mountedRef = useRef(false);
  const member = useAppSelector((state: { member: any }) => state.member);
  const [isLoadingDetail, setIsloadingDetail] = useState(false);
  const [resApiDetails, setResApiDetails] = useState(null);
  const [showFolioModal, setShowFolioModal] = useState(false);
  const [resDetails, setResDetails] = useState({
    checkInTime: "",
    checkOutTime: "",
    bedDesc: "",
    roomRates: null,
    cancelPolicies: {},
    cancelID: null,
    isCancellable: null,
  });

  const {
    checkInTime,
    checkOutTime,
    bedDesc,
    roomRates,
    cancelPolicies,
    cancelID,
  } = resDetails;
  const allHotels = useHotels();

  const [hotel, setHotel] = useState(reservation.hotel_details);
  const [hotelDisplayData, setHotelDisplayData] = useState({
    hotelName: "",
    address: "",
    addressMapLink: "",
    phone: null,
    imageUrl: fallbackImage,
  });
  const hrMemberlastName = useAppSelector((state: { member: any }) => {
    const member = state.member;
    return member && member.isLoggedIn && member.crmBasicProfile
      ? member.crmBasicProfile.lastName
      : null;
  });

  const memberID =
    member && member.isLoggedIn && member.crmBasicProfile
      ? member.crmBasicProfile.memberId
      : "";

  const lastName =
    reservation.guestLastName || hrMemberlastName || props.lastName;

  const stayDateStr = (() => {
    if (reservation.arrival) {
      const arrival = moment(reservation.arrival, resParseDateFormat);
      const departure = moment(reservation.departure, resParseDateFormat);
      const numberNights = departure.diff(arrival, "days");

      const nightStr = numberNights > 1 ? " Nights" : " Night";
      const displayArrival = arrival.format(resDisplayDateFormat);
      const displayDeparture = departure.format(resDisplayDateFormat);
      return `${displayArrival} - ${displayDeparture} (${numberNights}${nightStr})`;
    } else {
      return "";
    }
  })();

  const totalCostStr = (() => {
    if (roomRates) {
      const resDetailsRates =
        resDetails && resDetails.roomRates && resDetails.roomRates[0];
      const totalCost = resDetailsRates
        ? resDetailsRates.Total || resDetailsRates.total
        : 0;
      const currency =
        resDetailsRates?.CurrencyCode || resDetailsRates?.currencyCode || "";
      if (totalCost > 0) {
        return priceFormatter(totalCost, 2, currency);
      }
    }
    return null;
  })();

  const handleCancelReservation = () => {
    onCancel({
      openDialog: true,
      res: {
        ...reservation,
        ...{ cancelPolicies: cancelPolicies },
        ...{ hotel_details: hotel },
      },
    });
  };

  const getDetails = async (source: CancelTokenSource) => {
    mountedRef.current && setIsloadingDetail(true);
    try {
      let details;
      details = await fetchReservationDetails({
        resId: reservation.crs_reservation_id,
        hotelCode: hotel
          ? hotel.crs_code
          : reservation.details && reservation.details.HotelCode,
        lastName: lastName,
        source: source,
      });
      details = details.value;
      onReservationDetailLoad &&
        onReservationDetailLoad(reservation.crs_reservation_id, details);
      if (mountedRef.current) {
        if (details) {
          setResApiDetails(details);
          setResDetails({
            checkInTime: details.Policies
              ? details.Policies.CheckInTime
              : details.policies
              ? details.policies.checkInTime
              : "",
            checkOutTime: details.Policies
              ? details.Policies.CheckOutTime
              : details.policies
              ? details.policies.checkOutTime
              : "",
            bedDesc: details.RoomDescription
              ? details.RoomDescription.Name
              : details.roomDescription
              ? details.roomDescription.name
              : "",
            roomRates: details.RoomRates || details.roomRates,
            cancelPolicies:
              details.CancelPolicies || details.cancelPolicies || {},
            cancelID: details.CancelID || details.cancelId,
            isCancellable: details.isCancellable || details.isCancellable,
          });
        }
        // add hotel details if hotel info was not initially available from fetchContact api response
        const hotelCode =
          (details && (details.HotelCode || details.hotel?.id)) ||
          reservation?.hotelId;
        if (!hotel && hotelCode) {
          const reservedHotel = filter(allHotels, (h) => {
            return hotelCode == h.crs_code;
          });
          reservedHotel[0] && setHotel(reservedHotel[0]);
        }
        setIsloadingDetail(false);
      }
    } catch (e) {
      mountedRef.current && setIsloadingDetail(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const source = Axios.CancelToken.source();

    if (reservation) {
      if (mountedRef.current && !isLoadingDetail) {
        getDetails(source);
      }
    }

    // Cleanup on unmount.
    return () => {
      mountedRef.current = false;
      source.cancel(
        `Request cancelled for ${reservation.crs_reservation_id} details`
      );
    };
  }, []);

  useEffect(() => {
    if (hotel) {
      const address = hotel.address;
      const imageUrl =
        hotel.banner_images && hotel.banner_images.length
          ? hotel.banner_images[0].url
          : fallbackImage;
      const addressStr = address
        ? `${address.address_line1 || ""} ${address.address_line2 || ""} ${
            address.locality || ""
          }, ${address.administrative_area || ""}, ${
            address.country_code || ""
          } ${address.postal_code || ""}`
        : "";
      const addressParsed = addressStr.replace(/(<([^>]+)>)/gi, "").split(", ");
      const addressUrl = addressParsed.join("+").replace(/s/, "+");
      setHotelDisplayData({
        hotelName: hotel.name || "",
        address: addressStr,
        addressMapLink: `https://www.google.com/maps/dir/?api=1&destination=${addressUrl}`,
        phone: hotel.phone || null,
        imageUrl: imageUrl || fallbackImage,
      });
      onHotelDisplay && onHotelDisplay(hotel);
    }
  }, [hotel]);

  useEffect(() => {
    if (
      type === "cancelled" &&
      props.reservation &&
      props.reservation.details &&
      props.reservation.details.CancelID
    ) {
      setResDetails({
        ...resDetails,
        cancelID: props.reservation.details.CancelID,
      });
    }
  }, [type]);

  const addressArray = hotelDisplayData.address.split(", ");
  const nonNullAddressArray = addressArray.filter(Boolean);
  const addressWithoutNull = nonNullAddressArray.join(", ");
  return (
    <Container>
      <Row className="image-hotel-wrapper g-0">
        <Col sm={12} md={6} className="image-wrapper">
          <LazyLoadImage
            src={hotelDisplayData.imageUrl}
            alt={hotelDisplayData.hotelName}
          />
        </Col>
        <Col className="h-100">
          <Row className="h-100 px-md-4 ps-2 pt-3 g-0">
            <Col>
              <HotelName className="mb-2">
                {hotel && hotel.path ? (
                  <Link to={hotel.path.alias}>
                    {hotelDisplayData.hotelName}
                  </Link>
                ) : (
                  hotelDisplayData.hotelName
                )}
              </HotelName>
              <div>
                <span className="labels">Confirmation Number: </span>
                <span> {reservation.crs_reservation_id}</span>
              </div>
              {cancelID && (
                <div className="cancelled">
                  <span className="labels">Cancellation ID: </span>
                  <span> {cancelID}</span>
                </div>
              )}
              {isLoadingDetail ? (
                <Spinner animation="grow" size="sm" />
              ) : (
                <>
                  {bedDesc && (
                    <div>
                      <FontAwesomeIcon icon={faBed} className="me-1" />
                      <span>{bedDesc}</span>
                    </div>
                  )}
                </>
              )}
              <div>
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2 " />
                <span>
                  <u className="reservationDate">{stayDateStr}</u>
                </span>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Card>
        <Row className="mb-3 mt-3">
          <Col>
            {isLoadingDetail ? (
              <Spinner animation="grow" size="sm" />
            ) : (
              <>
                {checkInTime && (
                  <>
                    <span className="labels">Check-in: </span>
                    <span className="check-in">{checkInTime}</span>
                    <span className="labels">Check-out: </span>
                    <span>{checkOutTime}</span>
                  </>
                )}
              </>
            )}
          </Col>
          {hotelDisplayData.address && (
            <Col xs={12}>
              <span className="labels">Address: </span>
              <span>
                <u>
                  <a
                    href={hotelDisplayData.addressMapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="addressMap"
                  >
                    {addressWithoutNull}
                  </a>
                </u>
              </span>
            </Col>
          )}
          {hotelDisplayData.phone && (
            <Col xs={12} className="phone-link">
              <span className="labels">Phone: </span>
              <a href={`tel:${hotelDisplayData.phone}`}>
                {hotelDisplayData.phone}
              </a>
            </Col>
          )}
        </Row>

        {type === "upcoming" && (
          <Row className="mt-4">
            <Col className="mb-4 d-flex res-links">
              {member.isLoggedIn &&
                resApiDetails &&
                !cancelID &&
                resDetails?.isCancellable && (
                  <EditReservationButton
                    reservation={resApiDetails}
                    hotel={hotel}
                    confirmationID={reservation.crs_reservation_id}
                    memberId={memberID}
                  />
                )}
              {!cancelID && resDetails?.isCancellable && (
                <Button
                  variant="link"
                  size="sm"
                  className="cancel-reservation"
                  onClick={handleCancelReservation}
                >
                  CANCEL RESERVATION
                </Button>
              )}
            </Col>
            {!resDetails?.isCancellable && !isLoadingDetail ? (
              <div className="d-flex justify-content-center mt-2 mb-1">
                <div className="d-flex justify-content-center alert alert-blue text-left">
                  <div style={{ maxWidth: "95%" }}>
                    This reservation cannot be cancelled or modified.{" "}
                    {resDetails?.cancelPolicies?.description}
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </Row>
        )}
        {type === "recent" && hotel && hotel?.hotel_code && !cancelID && (
          <RequestFolioButton showFolioModal={setShowFolioModal} />
        )}
        {isLoadingDetail ? (
          <Spinner animation="grow" size="sm" />
        ) : (
          <>
            {totalCostStr && (
              <Row className="border-top border-bottom ms-0 me-0 mt-2 mb-4">
                {reservation?.redeemedPoints &&
                reservation?.redeemedPoints > 0 ? (
                  <FreeStay />
                ) : null}
                <Col
                  className={` ${
                    reservation?.redeemedPoints &&
                    reservation?.redeemedPoints > 0
                      ? ""
                      : "mt-3"
                  } mb-3 ps-0 pe-0 d-flex justify-content-between flex-wrap-wrap total-cost`}
                >
                  <span className="d-inline">Total For Stay:</span>
                  <span className="d-inline ">
                    {reservation?.redeemedPoints &&
                    reservation?.redeemedPoints > 0
                      ? `${reservation?.redeemedPoints.toLocaleString()} pts`
                      : totalCostStr}
                  </span>
                </Col>
              </Row>
            )}
          </>
        )}
      </Card>
      <RequestFolioModal
        show={showFolioModal}
        onHide={() => setShowFolioModal(false)}
        hotelId={hotel?.hotel_code}
        hotelName={hotel?.name}
        firstName={member?.crmBasicProfile?.firstName}
        lastName={member?.crmBasicProfile?.lastName}
        phone={member?.crmBasicProfile?.phones?.mobile || ""}
        email={member?.crmBasicProfile?.email}
        loyaltyNumber={member?.crmBasicProfile?.memberId}
        confirmation={props?.reservation?.crs_reservation_id}
        checkIn={props?.reservation?.arrival}
        checkout={props?.reservation?.departure}
      />
    </Container>
  );
};

export default React.memo(ReservationCard);
